import type { Account, AccountNode } from '@taler/api-client'
import { type CurrencyCode, isCurrencyCode, type Locale, resolveLocalized } from '@taler/domain'
import { ACCOUNT_CLASSES } from './account-form.ts'

export interface AccountRow {
  readonly id: string
  readonly code: string
  readonly label: string
  /** Raw per-locale names, for the edit dialog - the row's own `label` is already resolved to one locale. */
  readonly names: Account['names']
  readonly depth: number
  /** Direct children shown under the current filters. */
  readonly childCount: number
  readonly isArchived: boolean
  readonly isHidden: boolean
  readonly isPostable: boolean
  readonly isGroup: boolean
  readonly isSystem: boolean
  readonly accountClass: Account['accountClass']
  readonly accountType: Account['type']
  readonly currency: string | undefined
  readonly icon: string
  readonly color: string
  readonly parentId: string | undefined
}

/** Only ASSET/LIABILITY leaves carry one home currency every posting must match (AGENTS.md). */
export function homeCurrencyOf(row: AccountRow): CurrencyCode | undefined {
  if (row.isGroup || row.code === row.accountClass) return undefined
  if (row.accountClass !== 'ASSET' && row.accountClass !== 'LIABILITY') return undefined
  return row.currency !== undefined && isCurrencyCode(row.currency) ? row.currency : undefined
}

/** The account and every account beneath it, at any depth. */
export function subtreeIds(rows: readonly AccountRow[], rootId: string): ReadonlySet<string> {
  const childrenOf = new Map<string, string[]>()
  for (const row of rows) {
    if (row.parentId !== undefined)
      childrenOf.set(row.parentId, [...(childrenOf.get(row.parentId) ?? []), row.id])
  }
  const ids = new Set<string>()
  const pending = [rootId]
  for (let id = pending.pop(); id !== undefined; id = pending.pop()) {
    if (ids.has(id)) continue
    ids.add(id)
    pending.push(...(childrenOf.get(id) ?? []))
  }
  return ids
}

export interface FlattenOptions {
  readonly locale: Locale
  readonly showArchived: boolean
  readonly showHidden: boolean
}

function isVisible(account: Account, options: FlattenOptions): boolean {
  if (!options.showArchived && account.archivedAt !== undefined) return false
  if (!options.showHidden && account.hidden) return false
  return true
}

function label(account: Account, locale: Locale): string {
  return resolveLocalized(account.names, locale)?.name ?? account.code
}

/** A class root's own code is the bare class name (`ASSET`, never `ASSET.BANK`) - the one
 * structural invariant that tells it apart from an ordinary top-level account of the same
 * class (parentless "no parent (top level)" is a real, user-reachable choice in the create
 * dialog, so a class's array of top-level nodes can hold more than just its root). */
function isClassRoot(account: Account): boolean {
  return account.code === account.accountClass
}

/**
 * Pre-order walk, every row fully expanded - a statement is read whole, not navigated. Class
 * roots become section headers in accounting order (never the alphabetical order the wire
 * returns); any other parentless account of that class renders as an ordinary body row at the
 * same depth as the root's own children, rather than a second header for the same class.
 */
export function flattenTree(nodes: readonly AccountNode[], options: FlattenOptions): readonly AccountRow[] {
  const rows: AccountRow[] = []

  const pushRow = (account: Account, depth: number, childCount: number): void => {
    rows.push({
      id: account.id,
      code: account.code,
      label: label(account, options.locale),
      names: account.names,
      depth,
      childCount,
      isArchived: account.archivedAt !== undefined,
      isHidden: account.hidden,
      isPostable: account.isPostable,
      isGroup: account.isGroup,
      isSystem: account.isSystem,
      accountClass: account.accountClass,
      accountType: account.type,
      currency: account.currency,
      icon: account.icon,
      color: account.color,
      parentId: account.parentId,
    })
  }

  const walk = (level: readonly AccountNode[], depth: number): void => {
    for (const node of level) {
      const { account, children } = node
      if (!isVisible(account, options)) continue
      const visibleChildren = children.filter((child) => isVisible(child.account, options))
      pushRow(account, depth, visibleChildren.length)
      walk(visibleChildren, depth + 1)
    }
  }

  for (const accountClass of ACCOUNT_CLASSES) {
    const topLevel = nodes.filter((node) => node.account.accountClass === accountClass)
    const root = topLevel.find((node) => isClassRoot(node.account))
    const orphans = topLevel.filter((node) => node !== root)
    if (root !== undefined && isVisible(root.account, options)) {
      const visibleChildren = root.children.filter((child) => isVisible(child.account, options))
      pushRow(root.account, 0, visibleChildren.length + orphans.length)
      walk(visibleChildren, 1)
    }
    walk(orphans, 1)
  }

  return rows
}

export interface ParentOption {
  readonly id: string
  readonly code: string
  readonly name: string
  /** Ancestor names then this one, e.g. "Assets / Bank accounts / Revolut". */
  readonly breadcrumb: string
  readonly depth: number
  readonly accountClass: Account['accountClass']
  readonly currency: Account['currency']
  readonly icon: string
  readonly color: string
  /** Parent-only by design: an explicit group, a class root, or a category that already has sub-categories. */
  readonly isGroup: boolean
}

function isParentOnly(account: Account, hasChildren: boolean): boolean {
  if (account.isGroup || account.code === account.accountClass) return true
  const positionClass = account.accountClass === 'ASSET' || account.accountClass === 'LIABILITY'
  return !positionClass && hasChildren
}

/**
 * Every account that may parent a new one, in hierarchy order: archived accounts and
 * contact-backed accounts (with their subtrees) never qualify - contacts own those.
 */
export function listParentOptions(nodes: readonly AccountNode[], locale: Locale): readonly ParentOption[] {
  const out: ParentOption[] = []
  const walk = (level: readonly AccountNode[], trail: readonly string[]): void => {
    for (const { account, children } of level) {
      if (account.archivedAt !== undefined || account.type === 'CONTACT') continue
      const name = label(account, locale)
      const path = [...trail, name]
      out.push({
        id: account.id,
        code: account.code,
        name,
        breadcrumb: path.join(' / '),
        depth: trail.length,
        accountClass: account.accountClass,
        currency: account.currency,
        icon: account.icon,
        color: account.color,
        isGroup: isParentOnly(account, children.length > 0),
      })
      walk(children, path)
    }
  }
  for (const accountClass of ACCOUNT_CLASSES) {
    walk(
      nodes.filter((node) => node.account.accountClass === accountClass),
      [],
    )
  }
  return out
}

/** What the Parent field offers for a class: its groups (empty ones included), plus `keepId` (an account a row asked to add under). */
export function parentChoices(
  options: readonly ParentOption[],
  accountClass: Account['accountClass'],
  keepId?: string,
): readonly ParentOption[] {
  return options.filter(
    (option) => option.accountClass === accountClass && (option.isGroup || option.id === keepId),
  )
}

/** The class root: the fixed top of every class, and the default parent when no better group exists. */
export function classRootOf(options: readonly ParentOption[], accountClass: Account['accountClass']) {
  return options.find((option) => option.code === accountClass)
}
